// Порт ref SubBlockSettingsEditor:485-517 (align/size/toolbar/rotation) +
// renderBackdropSelector:275-339 (4 подложки, без blur — реф её не предлагает).
// Счётчик символов — наша добавка сверх рефа (contract §4.3): студийный потолок `DESIGN_TEXT_STUDIO_LIMIT` меряется
// по ОЧИЩЕННОМУ html — той же строке, что вернёт sanitizeDesignHtml (designTextLength),
// а не по сырой tiptap-разметке (сырую бек всё равно режет перед сохранением) и не
// по голому plain-тексту (иначе счётчик занижает и автор может превысить лимит на бек-стороне).
import { useMemo } from 'react'
import { Label } from '@teach/ui'
import type { DesignText } from '@/types/blocks'
import { DESIGN_LIMITS } from '@/features/editor/lib/designElements'
import { DESIGN_TEXT_STUDIO_LIMIT } from '@/features/editor/lib/designElementDefaults'
import { useContentLang } from '@/features/editor/hooks/useContentLang'
import { getLocalizedField } from '@/features/editor/lib/localizedField'
import { designTextLength } from '@/features/editor/lib/designHtml'
import { BackdropPicker } from './BackdropPicker'
import { CharacterCounter } from './CharacterCounter'
import { RotationSlider } from './RotationSlider'
import { SegmentedRow } from './SegmentedRow'
import { ALIGN_OPTIONS } from './segmented'
import { TextFormatToolbar } from './TextFormatToolbar'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  element: DesignText
  onUpdate: (patch: Partial<DesignText>) => void
}

const SIZES = [
  { value: 'medium' as const, label: 'S' },
  { value: 'large' as const, label: 'M' },
  { value: 'xlarge' as const, label: 'L' },
]

export const TextSettings = ({ element, onUpdate }: Props) => {
  const m = useMessages(messages)
  const contentLang = useContentLang()
  const content = getLocalizedField(element, 'content', contentLang) ?? ''
  // DOMPurify — не бесплатно, а этот счётчик перерисовывается на каждое нажатие клавиши.
  const used = useMemo(() => designTextLength(content), [content])

  return (
    <div className="space-y-4">
      <CharacterCounter
        used={used}
        limit={DESIGN_TEXT_STUDIO_LIMIT}
        tone={used > DESIGN_TEXT_STUDIO_LIMIT ? 'destructive' : 'primary'}
      />

      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">{m.align}</Label>
        <SegmentedRow
          options={ALIGN_OPTIONS}
          value={element.textAlign}
          onSelect={(textAlign) => onUpdate({ textAlign })}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">{m.textSize}</Label>
        <SegmentedRow
          options={SIZES}
          value={element.textSize}
          onSelect={(textSize) => onUpdate({ textSize })}
          dense
        />
      </div>

      <BackdropPicker value={element.backdrop} onSelect={(backdrop) => onUpdate({ backdrop })} />

      <TextFormatToolbar />

      <RotationSlider
        label={m.tilt}
        value={element.textRotation}
        limit={DESIGN_LIMITS.TEXT_ROTATION}
        onChange={(textRotation) => onUpdate({ textRotation })}
      />
    </div>
  )
}
